import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ArticleEditPage } from '@/components/admin/article-edit-page';
import { useAdminEditPageKey } from '@/hooks/use-admin-navigate';
import { adminHead } from '@/lib/seo';
import {
    adminGetArticleFn,
    adminListAuthorsFn,
    adminListCategoriesFn,
    adminPickAlertCoverImageFn,
} from '@/server/functions/admin';
import { adminArticleAnalyticsFn } from '@/server/functions/analytics';

const searchSchema = z.object({
    template: z.enum(['alert']).optional(),
    category: z.string().min(1).optional(),
    title: z.string().optional(),
});

export const Route = createFileRoute('/_admin/admin/articles_/$id')({
    component: ArticleEditRoute,
    validateSearch: searchSchema,
    head: ({ params, loaderData }) => {
        const isNew = params.id === 'new';
        const isAlert = isNew && loaderData?.template === 'alert';
        const title = isAlert
            ? 'מבזק חירום - צבע אדום'
            : isNew
              ? 'כתבה חדשה'
              : 'עריכת כתבה';
        return adminHead(title, `/admin/articles/${encodeURIComponent(params.id)}`);
    },
    staleTime: 0,
    preloadStaleTime: 0,
    loaderDeps: ({ search }) => ({
        template: search.template,
        category: search.category,
        title: search.title,
    }),
    loader: async ({ params, deps }) => {
        const isNew = params.id === 'new';
        const isAlert = isNew && deps.template === 'alert';
        const [articlePayload, authors, categories, alertCover, analytics] =
            await Promise.all([
                isNew
                    ? Promise.resolve(null)
                    : adminGetArticleFn({ data: { id: params.id } }),
                adminListAuthorsFn({ data: { limit: 200 } }),
                adminListCategoriesFn(),
                isAlert ? adminPickAlertCoverImageFn() : Promise.resolve(null),
                isNew
                    ? Promise.resolve(null)
                    : adminArticleAnalyticsFn({
                          data: { articleId: params.id },
                      }),
            ]);
        return {
            article: articlePayload?.article ?? null,
            relatedImages: articlePayload?.images ?? [],
            authors: authors.rows,
            categories: categories.rows,
            template: deps.template,
            preferredCategoryId: isNew ? deps.category : undefined,
            preferredTitle: isNew ? deps.title : undefined,
            alertCover,
            analytics,
        };
    },
});

function ArticleEditRoute() {
    const {
        article,
        relatedImages,
        authors,
        categories,
        template,
        preferredCategoryId,
        preferredTitle,
        alertCover,
        analytics,
    } = Route.useLoaderData();
    const formKey = useAdminEditPageKey(
        article ? `${article.id}:${String(article.updatedAt)}` : undefined,
        template === 'alert'
            ? `new-alert:${alertCover?.id ?? 'none'}`
            : preferredCategoryId
              ? `new:${preferredCategoryId}`
              : 'new',
    );
    return (
        <ArticleEditPage
            key={formKey}
            article={article}
            relatedImages={relatedImages}
            authors={authors}
            categories={categories}
            template={template}
            preferredCategoryId={preferredCategoryId}
            preferredTitle={preferredTitle}
            alertCover={alertCover}
            analytics={analytics}
        />
    );
}
